<template>
    <SwifterStruct>
        <span id="swifter-upload-head" />
        <div :class="isEmbedded ? '' : 'site-struct single'">
            <LoadingStruct
                is-dark
                :is-loading="!this.getInfos && token === undefined">
                <Card
                    v-if="token === undefined"
                    class="card-struct"
                    is-dark
                    mode="default"
                    :title="$t('swift')">
                    <div class="uk-flex uk-flex-center" uk-spinner />
                </Card>
                <Card
                    v-else-if="token === null"
                    class="card-struct"
                    is-dark
                    mode="default"
                    :title="$t('swift')">
                    <InfoBox mode="danger">
                        <Translate translation-key="swift.no.token" />
                    </InfoBox>
                </Card>
                <Card
                    v-else
                    :title="$t('swifter')"
                    class="card-struct"
                    is-dark
                    mode="default">
                    <LabelWithError
                        :error="nameError"
                        :label="$t('transfer.name') + '*'" />
                    <TextField
                        is-round
                        :value="bundleName"
                        autofocus
                        icon="hashtag"
                        label="transfer.name"
                        name="transfer-name"
                        rules="required|max:23"
                        is-label-hidden
                        @input="setName"
                        :validate="trigger"
                        @setValid="setValidName" />

                    <div class="uk-margin-top" />
                    <LabelWithError :label="$t('expires.in') + '*'" />

                    <div class="uk-grid-small uk-child-width-1-4" uk-grid>
                        <div v-for="option in [1, 3, 7, 30]" :key="option">
                            <span v-on:click="setExpiry(option)">
                                <LabelSelect
                                    is-round
                                    with-line-height
                                    :is-selected="
                                        bundleExpiresInDays === option
                                    ">
                                    <div class="uk-text-center paper-text">
                                        {{ option }}
                                        {{
                                            $t(
                                                option === 1
                                                    ? 'day'
                                                    : 'many.days'
                                            )
                                        }}
                                    </div>
                                </LabelSelect>
                            </span>
                        </div>
                    </div>

                    <div class="uk-margin-top" />
                    <SwitchButton
                        :value="bundleNotify"
                        @input="handleNotify"
                        id="transfer-notify"
                        :label="$t('transfer.notify')" />

                    <div class="uk-margin-top" />
                    <LabelWithError
                        :label="$t('emailAddress')"
                        is-optional
                        :error="bundleEmailError" />

                    <TextField
                        is-round
                        :value="bundleEmail"
                        is-optional
                        icon="mail"
                        label="emailAddress"
                        name="bundle-email"
                        rules="email"
                        is-label-hidden
                        @input="setEmail" />

                    <div class="uk-margin-top" />
                    <LabelWithError :label="$t('message')" is-optional />
                    <textarea
                        v-model="bundleMessage"
                        name="bundle-message"
                        class="uk-textarea" />

                    <div class="uk-margin-top" />
                    <LabelWithError
                        :error="filesError"
                        :label="$t('files') + '*'" />
                    <div
                        id="files-upload"
                        class="js-upload uk-placeholder uk-placeholder-hover uk-text-center cursor-pointer"
                        v-on:click="triggerInputDialog()">
                        <div>
                            <Icon
                                :ratio="2.2"
                                class="upload-icon"
                                icon="cloud-upload" />
                        </div>

                        <span class="uk-text-middle">{{
                            $t('x.drop.or.', {x: $t('files')})
                        }}</span>
                        <span class="uk-link uk-text-middle custom-link">{{
                            $t('select')
                        }}</span>

                        <div class="uk-hidden" uk-form-custom>
                            <input
                                id="files"
                                ref="files"
                                multiple
                                type="file"
                                v-on:change="handleFilesUpload()"
                                v-on:click="$refs.files.value = null" />
                        </div>

                        <div v-if="filesError" class="uk-margin-top" />
                        <LabelWithError
                            v-if="filesError"
                            :error="filesError"
                            :label="$t('files')" />
                    </div>

                    <div
                        class="uk-grid uk-grid-small uk-child-width-1-1"
                        uk-grid>
                        <div
                            v-for="(file, key) in files"
                            :key="'transferfile' + key">
                            <Card is-round is-dark mode="default">
                                <div class="uk-grid uk-grid-collapse" uk-grid>
                                    <div
                                        class="uk-width-expand uk-flex-middle uk-flex">
                                        <Icon
                                            v-if="false"
                                            class="uk-margin-small-right"
                                            icon="file" />
                                        {{ getFileNameOnly(file) }}
                                    </div>
                                    <div class="uk-width-auto">
                                        <span class="uk-margin-small-right" />
                                        <span
                                            :uk-tooltip="
                                                $t('x.delete', {x: $t('file')})
                                            "
                                            v-on:click="removeFile(key)">
                                            <Button is-round mode="danger">
                                                <Icon icon="trash" />
                                            </Button>
                                        </span>
                                    </div>
                                </div>
                            </Card>
                        </div>
                    </div>

                    <div class="uk-margin-medium-bottom" />
                    <Button
                        is-round
                        :action="createTransfer"
                        :is-disabled="uploadLoading"
                        :is-loading="uploadLoading"
                        :text="
                            $t(uploadLoading ? 'uploading' : 'transfer.create')
                        "
                        has-action
                        is-large
                        is-max
                        mode="primary" />

                    <progress
                        v-if="uploadLoading"
                        class="uk-progress"
                        :value="transferProgress"
                        max="100" />

                    <div v-if="uploadError" class="uk-margin-medium-bottom" />
                    <InfoBox v-if="uploadError" mode="danger">
                        <div
                            v-if="
                                uploadError.includes('507') ||
                                uploadError.includes('413')
                            ">
                            <Translate
                                translation-key="storage.size.limit.reached" />
                        </div>
                        <div v-else>
                            {{ uploadError }}
                        </div>
                    </InfoBox>

                    <Modal
                        :id="modalIdTransfer"
                        :title="$t('transfer.created')">
                        <div
                            v-if="false"
                            class="uk-text-center uk-margin-small-bottom">
                            <Translate
                                translation-key="transfer.created.message" />
                        </div>

                        <TextField
                            is-round
                            icon="code"
                            classes="cursor-pointer"
                            name="transfer-link"
                            label="link"
                            :tooltip="$t('copy.to.clipboard')"
                            v-model="bundleLink"
                            :value="bundleLink"
                            :on-click="codeToClipboard"
                            :action-function="codeToClipboard"
                            action-icon="copy-clipboard"
                            :action-tooltip="$t('copy.to.clipboard')" />

                        <div class="uk-margin-top" />
                        <Button
                            is-round
                            has-action
                            :action="openBundleLink"
                            mode="success"
                            is-large
                            is-max>
                            <Translate translation-key="transfer.open" />
                        </Button>

                        <hr class="uk-margin-top uk-margin-bottom" />
                        <Button
                            is-round
                            is-max
                            mode="primary"
                            class="uk-modal-close">
                            <Translate
                                translation-key="transfer.create.another" />
                        </Button>
                    </Modal>
                </Card>
            </LoadingStruct>
        </div>
    </SwifterStruct>
</template>

<script>
import LoadingStruct from '@/views/LoadingStruct.vue';
import Card from '@/components/generic/Card.vue';
import Icon from '@/components/generic/Icon.vue';
import LabelWithError from '@/components/generic/LabelWithError.vue';
import {isMobileOnly} from 'mobile-device-detect';
import TextField from '@/components/generic/TextField.vue';
import SwitchButton from '@/components/generic/SwitchButton.vue';
import LabelSelect from '@/components/LabelSelect.vue';
import Translate from '@/components/Translate.vue';
import Button from '@/components/generic/Button.vue';
import Modal from '@/components/generic/Modal.vue';
import InfoBox from '@/components/generic/InfoBox.vue';
import isEmail from 'is-email';
import UIkit from 'uikit';
import SwifterStruct from '@/views/swifter/SwifterStruct.vue';
import JSZip from 'jszip';
import axios from 'axios';

export default {
    name: 'SwifterUpload',
    components: {
        SwifterStruct,
        InfoBox,
        Modal,
        Button,
        Translate,
        LabelSelect,
        SwitchButton,
        TextField,
        LabelWithError,
        Icon,
        Card,
        LoadingStruct,
    },
    data() {
        return {
            isDeviceMobile: isMobileOnly,

            transferProgress: 0,

            trigger: 0,
            bundleName: '',
            nameError: null,
            bundleNameValid: false,
            bundleEmail: '',
            bundleNotify: false,
            bundleEmailValid: true,
            bundleEmailError: null,
            bundleExpiresInDays: 3,
            bundleLink: '',
            uploadError: null,
            uploadSuccess: false,
            uploadLoading: false,
            files: [],
            filesError: null,
            bundleMessage: '',

            modalIdTransfer: 'modal-transfer-created',

            token: undefined,
        };
    },
    created() {
        this.isTokenValid();
    },
    computed: {
        isUploadInProgress() {
            return false;
        },
        isEmbedded() {
            const embedded = this.$route.query.embedded;
            return embedded === 'true';
        },
    },
    methods: {
        initListeners() {
            let self = this;
            const intervalID = setInterval(function () {
                let element = document.getElementById('files-upload');
                if (!element) {
                    return;
                }

                clearInterval(intervalID);

                element.addEventListener('drop', self.dropListener, false);
                window.addEventListener(
                    'dragover',
                    self.dragoverListener,
                    false
                );
            }, 50);
        },
        isTokenValid() {
            const token = localStorage.getItem('swift_token');
            if (!token) {
                this.token = null;
                return;
            }

            const config = {
                headers: {
                    token: token,
                },
            };

            let http = axios.create();
            http.head(this.apiUrl + '/transfers', config)
                .then(() => {
                    this.token = token;

                    this.initListeners();
                })
                .catch(() => {});
        },
        openBundleLink() {
            event.stopPropagation();
            event.preventDefault();
            window.open(this.bundleLink, '_blank').focus();
        },
        setName(val) {
            this.bundleName = val;

            if (!val) {
                this.nameError = this.$t('x.required.message', {
                    x: this.$t('transfer.name'),
                });
            }
        },
        setValidName(valid) {
            this.bundleNameValid = valid;

            if (!valid) {
                return;
            }

            if (this.bundleName.length > 23) {
                this.nameError =
                    this.$t('transfer.name') +
                    ' ' +
                    this.$t('cant.be.longer.than.x.characters', {x: 23});
            } else {
                this.nameError = null;
            }
        },
        setExpiry(val) {
            this.bundleExpiresInDays = val;
        },
        handleNotify(val) {
            if (val !== false && val !== true) {
                return;
            }

            this.bundleNotify = val;
        },
        setEmail(val) {
            this.bundleEmail = val;

            if (val === '') {
                this.bundleEmailError = null;
                this.bundleEmailValid = true;
                return;
            }

            this.bundleEmailValid = isEmail(val);

            if (!this.bundleEmailValid) {
                this.bundleEmailError =
                    this.$t('emailAddress') + ' ' + this.$t('email.error');
            } else {
                this.bundleEmailError = null;
            }
        },
        getFileNameOnly(file) {
            return file.name;
        },
        removeFile(key) {
            this.files.splice(key, 1);
            this.$forceUpdate();
        },
        async createTransfer() {
            this.uploadSuccess = false;
            this.bundleLink = '';

            this.trigger++;
            if (!this.bundleNameValid) {
                this.nameError = this.$t('x.required.message', {
                    x: this.$t('transfer.name'),
                });
                return;
            }

            if (this.bundleName.length > 23) {
                this.nameError =
                    this.$t('transfer.name') +
                    ' ' +
                    this.$t('cant.be.longer.than.x.characters', {x: 23});
                return;
            }

            if (!this.bundleEmailValid) {
                return;
            }

            if (this.files.length === 0) {
                this.filesError = this.$t('x.required.message', {
                    x: this.$t('files'),
                });
                return;
            }
            this.filesError = null;

            this.uploadLoading = true;

            const zip = new JSZip();
            for (const file of this.files) {
                zip.file(file.relativePath || file.name, file);
            }

            const content = await zip.generateAsync({type: 'blob'});

            const zipFileName = `${this.bundleName}.zip`;
            const zipFile = new File([content], zipFileName, {
                type: 'application/zip',
            });

            let self = this;

            let formData = new FormData();
            formData.append('file', zipFile, zipFileName);

            if (this.bundleExpiresInDays) {
                formData.append('expiresInDays', this.bundleExpiresInDays);
            }

            if (this.bundleEmail) {
                formData.append('email', this.bundleEmail);
            }

            if (this.bundleMessage) {
                formData.append('message', this.bundleMessage);
            }

            if (this.bundleNotify) {
                formData.append('notify', this.bundleNotify);
            }

            const config = {
                onUploadProgress: function (progressEvent) {
                    self.transferProgress = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                },
                headers: {
                    token: this.token,
                },
            };
            this.requestTokenHeaders = null;
            let http = axios.create();
            http.post(this.apiUrl + '/transfers', formData, config)
                .then((response) => {
                    self.transferProgress = 100;
                    self.uploadError = null;
                    self.uploadSuccess = true;
                    this.uploadLoading = false;

                    this.requestTokenHeaders = response.headers;

                    UIkit.scroll('#swifter-upload-head', {offset: 0}).scrollTo(
                        '#swifter-upload-head'
                    );

                    this.bundleLink = response.headers.link;
                    UIkit.modal('#' + this.modalIdTransfer).show();

                    this.bundleName = '';
                    this.nameError = null;
                    this.bundleNameValid = false;
                    this.bundleEmail = '';
                    this.bundleEmailValid = true;
                    this.bundleEmailError = null;
                    this.bundleExpiresInDays = 3;
                    this.uploadError = null;
                    this.uploadSuccess = false;
                    this.uploadLoading = false;
                    this.filesError = null;
                    this.bundleMessage = '';
                    this.bundleNotify = false;

                    this.files.splice(0);
                })
                .catch((e) => {
                    self.checkGenericError(e);
                    self.transferProgress = 0;
                    self.uploadError = e.response
                        ? String(e.response.status)
                        : 'error';
                    self.uploadSuccess = false;
                    this.uploadLoading = false;
                });
        },
        codeToClipboard() {
            this.copyToClipboard(this.bundleLink);

            UIkit.notification({
                message: this.$t('copied.to.clipboard', {
                    description: this.$t('link'),
                }),
                status: 'info',
                pos: 'top-center',
                timeout: 5000,
            });
        },

        dropListener(e) {
            e.preventDefault();

            let element = document.getElementById('files-upload');
            element.classList.remove('dragover');

            this.processFilesUpload(e.dataTransfer.files);

            /*
            if (items && items.length) {
                for (const item of items) {
                    const entry =
                        item.webkitGetAsEntry && item.webkitGetAsEntry();
                    if (entry) {
                        this.traverseFileTree(entry);
                    }
                }
            }
             */
        },

        async traverseFileTree(entry, path = '') {
            if (entry.isFile) {
                await new Promise((resolve) => {
                    entry.file((file) => {
                        file.relativePath = path + file.name;
                        this.processFilesUpload([file]);
                        resolve();
                    });
                });
            } else if (entry.isDirectory) {
                const reader = entry.createReader();
                reader.readEntries((entries) => {
                    for (const subEntry of entries) {
                        this.traverseFileTree(
                            subEntry,
                            path + entry.name + '/'
                        );
                    }
                });
            }
        },

        processFilesUpload(files) {
            for (let i = 0, j = files.length; i < j; i++) {
                if (this.fileNameExists(files[i])) {
                    UIkit.notification({
                        message: this.$t('file.x.already.added', {
                            x: this.getFileNameOnly(files[i]),
                        }),
                        status: 'info',
                        pos: 'top-center',
                        timeout: 5000,
                    });

                    continue;
                }

                this.files.push(files[i]);
            }

            if (files) {
                this.filesError = null;
            }
        },
        fileNameExists(fileName) {
            const fileNameOnly = this.getFileNameOnly(fileName);

            for (let i = 0, j = this.files.length; i < j; i++) {
                if (this.getFileNameOnly(this.files[i]) === fileNameOnly) {
                    return true;
                }
            }

            return false;
        },
        dragoverListener(e) {
            e.preventDefault();

            let element = document.getElementById('files-upload');
            element.classList.add('dragover');
        },

        triggerInputDialog() {
            document.getElementById('files').click();
        },
        handleFilesUpload() {
            let files = this.$refs['files'].files;
            this.processFilesUpload(files);
        },
    },
};
</script>

<style lang="scss" scoped>
.card-struct {
    border-left-width: 3px;
    border-top-width: 3px;
    border-right-width: 3px;
    border-bottom-width: 3px;
    border-bottom-style: solid;
    border-radius: $border-radius-next;

    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    background-color: #f9f9f9;
}

.uk-textarea,
.uk-placeholder {
    border-radius: $border-radius-next;
}
</style>
